import { isNative } from './platform';
import { PORTAL } from './portal';

/**
 * Where the refresh token lives:
 *  - web: nowhere in JS. It's an httpOnly cookie the server sets; we just call /refresh.
 *  - native: the iOS Keychain / Android Keystore via secure-storage.
 *  - portal builds: localStorage. The game runs in a third-party iframe where our
 *    SameSite=Strict cookie is never sent, so the token travels in the body like native.
 */
const KEY = 'snakefeast.refresh';
export const bodyTokens = isNative || !!PORTAL;

const local = {
  get: () => {
    try {
      return localStorage.getItem(KEY);
    } catch {
      return null;
    }
  },
  set: (v: string | null) => {
    try {
      if (v) localStorage.setItem(KEY, v);
      else localStorage.removeItem(KEY);
    } catch {
      /* storage blocked: session lasts until reload */
    }
  },
};

/**
 * The plugin module, not the plugin: a Capacitor plugin is a Proxy that answers every property, `then`
 * included, so returning it from an async function (or awaiting it) treats it as a promise that never
 * settles, and app start hung on the logo waiting for the stored token.
 */
const secure = () => import('@aparajita/capacitor-secure-storage');

export const tokenStore = {
  async get(): Promise<string | null> {
    if (PORTAL && !isNative) return local.get();
    if (!isNative) return null;
    const { SecureStorage } = await secure();
    return ((await SecureStorage.get(KEY).catch(() => null)) as string | null) ?? null;
  },
  async set(token: string | undefined): Promise<void> {
    if (PORTAL && !isNative) return local.set(token ?? null);
    if (!isNative || !token) return;
    const { SecureStorage } = await secure();
    await SecureStorage.set(KEY, token);
  },
  async clear(): Promise<void> {
    if (PORTAL && !isNative) return local.set(null);
    if (!isNative) return;
    const { SecureStorage } = await secure();
    await SecureStorage.remove(KEY).catch(() => {});
  },
};
